import { HttpClient } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { KeyValueStorageInterface } from '@aws-amplify/core';
import { validateAllowedOrigins } from '@lassi/util';
import { GoogleTagManagerConfig } from 'angular-google-tag-manager';
import {
  catchError,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { IAppConfig, IAppVersion } from './model';

export const GAConfig = new InjectionToken<
  Observable<GoogleTagManagerConfig | null>
>('Google Tag Manager Config', {
  factory: () =>
    inject(HttpClient)
      .get<GoogleTagManagerConfig>('.well-known/gtm.json')
      .pipe(
        catchError((err) => {
          console.error(err);
          return of(null);
        }),
        shareReplay(1),
      ),
});

/**
 * We are using sessionStorage to store user session, because we want the user to be logged out
 * when the browser tab is closed.
 *
 * However, this makes the "remember device" functionality inconsistent, because the remember device data is also stored in sessionStorage and gets cleared if the browser is closed.
 * Now we store remember device data in localStorage, so it persists across browser sessions. We store normal login data in sessionStorage as before.
 */
export class CognitoKeyValueStorage implements KeyValueStorageInterface {
  localStorage = window.localStorage;
  sessionStorage = window.sessionStorage;

  private getStorage(key: string) {
    const localKeys = ['deviceKey', 'deviceGroupKey', 'randomPasswordKey'];
    return localKeys.some((k) => key.endsWith(k))
      ? this.localStorage
      : this.sessionStorage;
  }

  async setItem(key: string, value: string) {
    this.getStorage(key).setItem(key, value);
  }

  async getItem(key: string): Promise<string | null> {
    return this.getStorage(key).getItem(key);
  }

  get length() {
    return this.localStorage.length + this.sessionStorage.length;
  }

  async clear() {
    this.localStorage.clear();
    this.sessionStorage.clear();
  }
  key(_index: number): string | null {
    throw new Error('Method not implemented.');
  }
  async removeItem(key: string) {
    this.getStorage(key).removeItem(key);
  }
}

export const APPConfig = new InjectionToken<Observable<IAppConfig>>(
  'App Config',
  {
    factory: () => {
      const http = inject(HttpClient);
      return http
        .get<IAppConfig>('.well-known/config.json')
        .pipe(shareReplay(1));
    },
  },
);

export interface IAmplifyOutputs {
  custom: {
    embedded: {
      landata_origin: string;
      spear_origin: string;
    };
    captcha: {
      apiKey: string;
      script: string;
    };
  };
}

export const AMPLIFY_OUTPUTS = new InjectionToken<Observable<IAmplifyOutputs>>(
  'App Config',
  {
    factory: () => {
      const http = inject(HttpClient);
      return http
        .get<IAmplifyOutputs>('.well-known/amplify_outputs.json')
        .pipe(shareReplay(1));
    },
  },
);

export const EmbeddedMapOrigins = new InjectionToken('Embedded map origins', {
  factory: () => {
    const hostOrigin$ = inject(ActivatedRoute).queryParamMap.pipe(
      map((params) => params.get('origin') || ''),
      shareReplay(1),
    );

    const amplifyOutputs$ = inject(AMPLIFY_OUTPUTS);

    return hostOrigin$.pipe(take(1)).pipe(
      switchMap((hostOrigin) => {
        if (window.location.hostname === 'localhost') {
          return of({
            landata_origin: 'http://localhost:4201',
            spear_origin: validateAllowedOrigins(
              hostOrigin,
              'http://localhost:4201',
            ),
          });
        }

        return amplifyOutputs$.pipe(
          map((outputs) => ({
            landata_origin: outputs.custom.embedded.landata_origin,
            spear_origin: validateAllowedOrigins(
              hostOrigin,
              outputs.custom.embedded.spear_origin,
            ),
          })),
        );
      }),
    );
  },
});

export const AppFeatures = new InjectionToken<
  Observable<IAppConfig['features']>
>('App Features', {
  factory: () => inject(APPConfig).pipe(map((config) => config.features)),
});

export const AppVersion = new InjectionToken<Observable<IAppVersion>>(
  'APP_VERSION',
  {
    factory: () => {
      const http = inject(HttpClient);
      return http
        .get<IAppVersion>('.well-known/version.json')
        .pipe(shareReplay(1));
    },
  },
);
